import {ILogService} from "angular";
import _ from "lodash";
import {regNavn} from "./tredjepartsTjenestenavn";
import {Debugger} from "debug";

const debug: Debugger = require('debug')('MeldingTilBruker');

// timeout bør støtte "global" melding som blir stående til timeout er passert og
// en for lokale meldinger, f.eks. "innkalling sendt" som blir borte enten ved sidenavigasjon eller etter n sekunder.

export enum Varighet { klikkBort, sideskift, timeout, timeout_eller_navigasjon}

export type VedKlikk = (() => boolean) | null;

export class Melding {
    constructor(public melding: string, public varighet: Varighet, public vedKlikk: VedKlikk) {
    }
}

/** Tjeneste for fremvisning av meldinger til bruker.
 * Registrerte meldinger viser vhja meldinger_til_bruker direktivet */
//ng2 ekvivalent: src/scripts/felles-ng2/tjenester/MeldingTilBrukerNg2.ts
export class MeldingTilBruker {
    static regNavn = "meldingTilBruker";

    static forInjisering() {
        return [regNavn.ng.$log, MeldingTilBruker]
    }

    constructor(private $log: ILogService) {
    }

    private _feil: Melding[] = [];
    private _advarsel: Melding[] = [];
    private _info: Melding[] = [];
    private _callback: Function[] = [];

    //offentlige metoder

    /**
     * Viser infomelding til bruker.
     * Dersom det allerede er reg en melding med samme tekst registreres det IKKE noen ny melding.
     *
     * melding: meldingen som skal vises, i ren tekst
     * varighet: hvor lenge meldingen skal vises
     * vedKlikk: callback som kalles dersom bruker klikker på melding, meldingen skjules dersom callback returnerer sann
     */
    infomelding(melding: string, varighet: Varighet = Varighet.timeout, vedKlikk: VedKlikk = null): Melding {
        const meldingsobjekt = new Melding(melding, varighet, vedKlikk);
        if (MeldingTilBruker.finnesFraFør(meldingsobjekt, this._info)) return meldingsobjekt;

        this._info.push(meldingsobjekt);
        this.kjørCallback();
        return meldingsobjekt;
    }

    /**
     * Viser advarselmelding til bruker.
     * Dersom det allerede er reg en melding med samme tekst registreres det IKKE noen ny melding.
     *
     * melding: meldingen som skal vises, i ren tekst
     * varighet: hvor lenge meldingen skal vises
     * vedKlikk: callback som kalles dersom bruker klikker på melding, meldingen skjules dersom callback returnerer sann
     */
    advarselmelding(melding: string, varighet: Varighet = Varighet.timeout, vedKlikk: VedKlikk = null): Melding {
        const meldingsobjekt = new Melding(melding, varighet, vedKlikk);
        this.$log.warn(melding);
        if (MeldingTilBruker.finnesFraFør(meldingsobjekt, this._advarsel)) return meldingsobjekt;
        this._advarsel.push(meldingsobjekt);
        this.kjørCallback();
        return meldingsobjekt;
    }

    /**
     * Viser feilmelding til bruker.
     * Dersom det allerede er reg en melding med samme tekst registreres det IKKE noen ny melding.
     *
     * melding: meldingen som skal vises, i ren tekst
     * varighet: hvor lenge meldingen skal vises
     * vedKlikk: callback som kalles dersom bruker klikker på melding, meldingen skjules dersom callback returnerer sann
     */
    feilmelding(melding: string, varighet: Varighet = Varighet.timeout, vedKlikk: VedKlikk = null, skrivTilLog = true): Melding {
        const meldingsobjekt = new Melding(melding, varighet, vedKlikk);
        if (skrivTilLog) this.$log.error(melding);
        if (MeldingTilBruker.finnesFraFør(meldingsobjekt, this._feil)) return meldingsobjekt;
        this._feil.push(meldingsobjekt);
        this.kjørCallback();
        return meldingsobjekt;
    }

    vedFeil(melding: string) {
        return (feil: any) => {
            this.feilmelding(melding, Varighet.timeout, null, false);
            this.$log.error(`${melding}, feil: ${this.exceptionTilStreng(feil)}`);
        }
    }


    infomeldinger() {
        return this._info;
    }

    advarselmeldinger() {
        return this._advarsel;
    }

    feilmeldinger() {
        return this._feil;
    }

    fjernMelding(melding: Melding) {
        _.remove(this._feil, m => m === melding);
        _.remove(this._advarsel, m => m === melding);
        _.remove(this._info, m => m === melding);
        this.kjørCallback();
    }

    fjernMeldingerMedVarighet(varighet: Varighet) {
        _.remove(this._feil, {varighet: varighet});
        _.remove(this._advarsel, {varighet: varighet});
        _.remove(this._info, {varighet: varighet});
        this.kjørCallback();
    }

    tømFeilmeldinger() {
        this._feil = [];
        this.kjørCallback();
    }

    tømAdvarselmeldinger() {
        this._advarsel = [];
        this.kjørCallback();
    }

    tømInfomeldinger() {
        this._info = [];
        this.kjørCallback();
    }

    antallMeldinger() {
        return this._feil.length + this._info.length + this._advarsel.length
    }

    /** Registrerer callback som kalles når melding registreres.
     * Returnerer metode for å fjerne callbacket */
    registrerCallback(callback): Function {
        this._callback.push(callback);
        return () => {
            _.pull(this._callback, callback);
        }
    }

    private kjørCallback() {
        this._callback.forEach(callback => {
            callback();
        });
    }

    private exceptionTilStreng(feil: any): string {
        let melding = '';
        try {
            if (_.isNil(feil))
                return melding;

            if (_.has(feil, 'message'))
                melding = feil.message;
            if (_.has(feil, 'stack'))
                melding = `${melding}, stack: ${feil.stack}`;
            if (_.isEmpty(melding))
                melding = JSON.stringify(feil)
            if (melding == '{}')
                melding = _.toString(feil)

            return melding;

        } catch (e) {
            return 'feil under generering av feilmelding';
        }
    }

    private static finnesFraFør(nyMelding: Melding, eksisterende: Melding[]): boolean {
        if (eksisterende.some(eksisterende => eksisterende.melding == nyMelding.melding)) {
            debug("infomelding eksiterer, dropper");
            return true;
        }
        return false;
    }
}

